import React from 'react';
import { XMarkIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { Button } from './Button';

interface DownloadConfirmationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title?: string;
    message?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    isLoading?: boolean;
}

export const DownloadConfirmationModal: React.FC<DownloadConfirmationModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    title = 'Access Confirmation',
    message = 'To proceed with this export, we require your authorization to securely process project vectors.',
    confirmLabel = 'Authorize',
    cancelLabel = 'Abort',
    isLoading = false,
}) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 overflow-hidden">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/80 backdrop-blur-md animate-in fade-in duration-300"
                onClick={onClose}
            />

            {/* Modal Container */}
            <div className="relative glass-panel rounded-[2rem] shadow-[0_0_80px_rgba(0,0,0,0.6)] border border-white/10 max-w-md w-full overflow-hidden animate-in zoom-in-95 fade-in slide-in-from-bottom-8 duration-300 text-center">
                {/* Visual Accent */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-primary to-transparent opacity-50"></div>

                {/* Content */}
                <div className="p-10 relative z-10">
                    <div className="flex flex-col items-center">
                        <div className="mb-8 p-5 rounded-3xl bg-primary/10 text-primary border border-primary/20 relative">
                            <div className="absolute inset-0 bg-primary/20 blur-xl rounded-full"></div>
                            <ShieldCheckIcon className="h-12 w-12 relative z-10" />
                        </div>

                        <h3 className="text-2xl font-black text-white mb-4 tracking-tight uppercase">
                            {title}
                        </h3>
                        
                        <p className="text-text-secondary leading-relaxed font-medium mb-10 text-sm">
                            {message}
                        </p>

                        <div className="w-full space-y-4">
                            <Button
                                onClick={onConfirm}
                                loading={isLoading}
                                className="w-full h-14 rounded-2xl bg-primary hover:bg-orange-500 text-white font-black tracking-widest text-[11px] uppercase shadow-[0_0_30px_rgba(255,107,0,0.3)] transition-all active:scale-[0.98] border-none"
                            >
                                {confirmLabel}
                            </Button>
                            
                            <button
                                onClick={onClose}
                                disabled={isLoading}
                                className="w-full h-14 rounded-2xl text-text-secondary hover:text-white font-black tracking-widest text-[11px] uppercase transition-all hover:bg-white/5 border border-white/5 flex items-center justify-center"
                            >
                                {cancelLabel}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-5 right-5 text-text-secondary hover:text-white transition-colors p-2 hover:bg-white/10 rounded-xl"
                >
                    <XMarkIcon className="h-6 w-6" />
                </button>
            </div>
        </div>
    );
};
